import React, { useState, useRef } from "react";
import api, { apiErrorMsg } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Upload, FileText, Download, AlertTriangle, CheckCircle } from "lucide-react";
import { toast } from "sonner";

export default function BulkImportDialog({ open, onClose, onDone }) {
  const [csvText, setCsvText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setCsvText(text);
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/admin/properties/bulk-import-template");
      const blob = new Blob([r.data.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = r.data.filename || "template.csv";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMsg(e, "Failed to download")); }
  };

  const submit = async () => {
    if (!csvText.trim()) { toast.error("Paste CSV or upload a file"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/properties/bulk-import", { csv: csvText });
      setResult(r.data);
      if (r.data.inserted > 0) toast.success(`Imported ${r.data.inserted} properties`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} rows failed — see details`);
      onDone?.();
    } catch (e) { toast.error(apiErrorMsg(e, "Import failed")); }
    setBusy(false);
  };

  const reset = () => { setCsvText(""); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && (onClose?.(), reset())}>
      <DialogContent className="max-w-3xl rounded-sm" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-2xl flex items-center gap-2">
            <Upload size={20} /> Bulk Import Properties
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[#52525B]">
            Sab columns optional hain. Sirf jo aapke paas data hai woh CSV me daalo — baaki defaults se fill ho jayega. Use semicolons in <code className="text-[11px] bg-[#F4F4F5] px-1 rounded">images</code> and <code className="text-[11px] bg-[#F4F4F5] px-1 rounded">tags</code> fields.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <>
            {/* v2.88 — Persistent yellow banner: Apartment/Flat rows are
                normalized to Builder Floor per current catalog. */}
            <div
              className="flex items-start gap-2 rounded-sm border border-[#FDE68A] bg-[#FFFBEB] p-3 text-[12px] text-[#78350F]"
              data-testid="bulk-import-normalization-notice"
              role="note"
            >
              <AlertTriangle size={14} className="mt-0.5 flex-none text-[#B45309]" />
              <div className="leading-relaxed">
                <b>Heads up:</b> <code className="text-[11px] bg-white/60 px-1 rounded border border-[#FDE68A]">Apartment</code>,{" "}
                <code className="text-[11px] bg-white/60 px-1 rounded border border-[#FDE68A]">Flat</code> and{" "}
                <code className="text-[11px] bg-white/60 px-1 rounded border border-[#FDE68A]">Flat/Apartment</code>{" "}
                values in <code className="text-[11px] bg-white/60 px-1 rounded border border-[#FDE68A]">property_type</code>{" "}
                are automatically normalized to <b>Builder Floor</b> per the current catalog.{" "}
                <code className="text-[11px] bg-white/60 px-1 rounded border border-[#FDE68A]">Full Building</code> is normalized to <b>Building</b>. Original values are preserved in the audit log.
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-sm" data-testid="bulk-pick-file">
                <FileText size={14} className="mr-1.5" /> Choose CSV file
              </Button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="bulk-file-input" />
              <Button variant="ghost" onClick={downloadTemplate} className="rounded-sm" data-testid="bulk-download-template">
                <Download size={14} className="mr-1.5" /> Download template CSV
              </Button>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1">CSV content</div>
              <Textarea
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                rows={12}
                className="rounded-sm font-mono text-[11px]"
                placeholder="title,price,area,bhk,property_type,size_sqft&#10;Sample 2BHK,4500000,Uttam Nagar,2 BHK,Builder Floor,850"
                data-testid="bulk-csv-textarea"
              />
              <div className="text-[11px] text-[#52525B] mt-1">{csvText.split(/\r?\n/).filter(Boolean).length - (csvText ? 1 : 0)} rows queued</div>
            </div>
          </>
        )}

        {result && (
          <div className="space-y-3" data-testid="bulk-import-result">
            <div className="grid grid-cols-3 gap-2">
              <Stat icon={<CheckCircle size={14} className="text-[#15803D]" />} label="Imported" value={result.inserted} accent="green" />
              <Stat icon={<AlertTriangle size={14} className="text-[#DC2626]" />} label="Errors" value={result.errors?.length || 0} accent={result.errors?.length ? "red" : ""} />
              <Stat icon={<FileText size={14} />} label="Total Rows" value={result.total_rows} />
            </div>
            {result.errors?.length > 0 && (
              <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-sm p-3 max-h-48 overflow-y-auto">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#B91C1C] mb-1.5">Failed rows</div>
                <ul className="space-y-1 text-[11px] text-[#991B1B]">
                  {result.errors.map((e, i) => (
                    <li key={i} className="font-mono"><b>Row {e.row}:</b> {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { onClose?.(); reset(); }} className="rounded-sm" data-testid="bulk-close">
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button onClick={submit} disabled={busy || !csvText.trim()} className="btn-brand rounded-sm" data-testid="bulk-submit">
              <Upload size={14} className="mr-1.5" /> {busy ? "Importing…" : "Import Properties"}
            </Button>
          )}
          {result && (
            <Button onClick={reset} className="btn-brand rounded-sm" data-testid="bulk-import-another">
              Import another
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ icon, label, value, accent }) => (
  <div className={`bg-white border rounded-sm p-3 ${accent === "green" ? "border-[#A7F3D0] bg-[#F0FDF4]" : accent === "red" ? "border-[#FECACA] bg-[#FEF2F2]" : "border-[#E4E4E7]"}`}>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] inline-flex items-center gap-1">{icon} {label}</div>
    <div className="font-heading font-bold text-2xl text-[#09090B] font-mono-data">{value}</div>
  </div>
);
